import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { FC } from "react";
import { useSelector } from "react-redux";

import { ArrowForward } from "@livechat/design-system-icons";
import {
  DesignToken,
  Icon,
  ProgressCircle,
  RadiusToken,
  SpacingToken,
  Text,
  TransitionDurationToken,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { SubscriptionPhase } from "constants/subscription-phase";
import { trialPeriod } from "constants/trial";
import { getCurrentSubscriptionDaysLeft, selectSubscriptionPhase } from "store/entities/subscriptions";
import { withVar } from "themes/helpers";
import { plural } from "utils/string";

interface IProps {
  stepsCompleted: number;
  stepsToComplete: number;
  onDrawerOpen: VoidFunction;
}

export const OnboardingBar: FC<IProps> = ({ stepsCompleted, stepsToComplete, onDrawerOpen }) => {
  const trialDaysLeft = useSelector(getCurrentSubscriptionDaysLeft);
  const phase = useSelector(selectSubscriptionPhase);
  const onboardingCompleted = stepsCompleted === stepsToComplete;
  const isTrialWithCC = phase === SubscriptionPhase.TrialCC;
  const isTrialWithoutCC = phase === SubscriptionPhase.TrialNoCC;
  const trialProgress = Math.round((trialDaysLeft / trialPeriod) * 100);
  const displayAsWarning = trialDaysLeft <= 7 && phase === SubscriptionPhase.TrialNoCC;

  return (
    <Wrapper
      id="onboarding-bar"
      onClick={onDrawerOpen}
      displayAsWarning={displayAsWarning}
      role="button"
      extraPaddingLeft={!isTrialWithoutCC}
    >
      {isTrialWithoutCC && (
        <Flex gap={withVar(SpacingToken.Spacing1)} alignItems="center" marginRight={withVar(SpacingToken.Spacing3)}>
          <Flex css={{ position: "relative" }} justifyContent="center">
            <StyledProgressCircle progressValue={trialProgress} size="small" />
            <Flex
              justifyContent="center"
              alignItems="center"
              width="18px"
              height="18px"
              borderRadius="50%"
              bg={withVar(DesignToken.SurfaceSecondaryDefault)}
              css={{ position: "absolute", top: 3, left: 3 }}
            >
              <Text size="sm" bold customColor={withVar(DesignToken.ContentBasicPrimary)}>
                {trialDaysLeft}
              </Text>
            </Flex>
          </Flex>
          <Text bold>trial {plural(trialDaysLeft, "day", "days")} left</Text>
        </Flex>
      )}
      {onboardingCompleted ? (
        <>{isTrialWithCC && <Text bold>Explore HelpDesk</Text>}</>
      ) : (
        <Text
          bold
          customColor={withVar(DesignToken.ActionPrimaryDefault)}
          css={css`
            align-self: center;
          `}
        >
          {stepsCompleted} of {stepsToComplete} completed
        </Text>
      )}
      <ArrowForwardIcon source={ArrowForward} size="large" />
    </Wrapper>
  );
};

const ArrowForwardIcon = styled(Icon)`
  margin-right: ${withVar(SpacingToken.Spacing1)};
  transition: transform ${withVar(TransitionDurationToken.Moderate1)};
`;

const slideBottomAnimation = keyframes`
  0% {
    transform: translateY(-20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0px);
    opacity: 1;
  }`;

const Wrapper = styled(Flex)<{ displayAsWarning: boolean; extraPaddingLeft: boolean }>`
  display: flex;
  align-items: "center";
  gap: ${withVar(SpacingToken.Spacing3)};

  cursor: pointer;
  padding: ${withVar(SpacingToken.Spacing1)};
  padding-left: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (p: any) => (p.extraPaddingLeft ? withVar(SpacingToken.Spacing3) : withVar(SpacingToken.Spacing1))
  };
  border-radius: ${withVar(RadiusToken.Radius4)};
  margin-right: ${withVar(SpacingToken.Spacing4)};
  transition: background-color ${withVar(TransitionDurationToken.Moderate1)};
  background-color: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (p: any) =>
      withVar(p.displayAsWarning ? DesignToken.SurfaceAccentEmphasisLowWarning : DesignToken.SurfaceSecondaryDefault)
  };

  &:hover {
    background-color: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (p: any) => withVar(p.displayAsWarning ? DesignToken.DecorYellow200 : DesignToken.SurfaceSecondaryHover)
    };

    ${ArrowForwardIcon} {
      transform: translateX(3px);
    }
  }

  &.slide-bottom {
    animation: ${slideBottomAnimation} 0.4s ease-out;
  }
`;

const StyledProgressCircle = styled(ProgressCircle)`
  height: 24px;
  width: 24px;

  circle {
    stroke-width: 1;
  }
`;
